/**
 * Sistema de Conferência E-commerce — Painel de Estoque Baixo
 * Mostra produtos vendidos nos últimos 6 meses com estoque zerado/acabando
 * e notifica quando produto vai acabar em ≤7 dias baseado na média de vendas
 */

import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Container, Row, Col, Card, Table, Form, InputGroup, Badge, Spinner, Image } from 'react-bootstrap';
import { fetchProdutosEstoqueBaixo } from '../../services/api';

// Ícones inline (sem dependência)
const ICONS = {
  zerado: '🔴',
  acabando: '🟡',
  critico: '🟠',
  total: '📦',
  busca: '🔍',
  alerta: '⚠️',
};

// Cores dos badges
const RISCO_CONFIG = {
  critico: { bg: '#dc3545', label: 'Crítico', badgeBg: 'danger' },
  alto: { bg: '#fd7e14', label: 'Alto', badgeBg: 'warning' },
  medio: { bg: '#ffc107', label: 'Médio', badgeBg: 'warning' },
  baixo: { bg: '#28a745', label: 'Baixo', badgeBg: 'success' },
  sem_dados: { bg: '#6c757d', label: 'Sem dados', badgeBg: 'secondary' },
};

const ConferenciaEcommerce = () => {
  const [filtroAtivo, setFiltroAtivo] = useState('todos');
  const [busca, setBusca] = useState('');
  const [buscaDebounced, setBuscaDebounced] = useState('');
  const [ordenacao, setOrdenacao] = useState('risco'); // risco, estoque, vendas, nome

  // Debounce de busca
  const debounceRef = React.useRef(null);
  const handleBusca = (valor) => {
    setBusca(valor);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setBuscaDebounced(valor), 400);
  };

  // Fetch dos dados
  const { data: resultado, isLoading, error } = useQuery({
    queryKey: ['estoque-baixo', filtroAtivo, buscaDebounced],
    queryFn: () => fetchProdutosEstoqueBaixo({ filtro: filtroAtivo, busca: buscaDebounced }),
    staleTime: 2 * 60 * 1000, // 2 minutos
    refetchInterval: 5 * 60 * 1000, // Atualiza a cada 5 min
  });

  const resumo = resultado?.resumo || { total: 0, zerados: 0, acabando: 0, critico7d: 0 };
  const produtos = resultado?.data || [];

  // Ordenação local
  const produtosOrdenados = useMemo(() => {
    const lista = [...produtos];
    switch (ordenacao) {
      case 'estoque':
        lista.sort((a, b) => a.estoque - b.estoque);
        break;
      case 'vendas':
        lista.sort((a, b) => b.totalVendas - a.totalVendas);
        break;
      case 'nome':
        lista.sort((a, b) => (a.nome || '').localeCompare(b.nome || ''));
        break;
      case 'dias':
        lista.sort((a, b) => a.diasEstoque - b.diasEstoque);
        break;
      default: // 'risco' - já vem ordenado do backend
        break;
    }
    return lista;
  }, [produtos, ordenacao]);

  // Estilo de linha baseado no risco
  const getRowStyle = (produto) => {
    if (produto.estoque === 0) return { backgroundColor: 'rgba(220, 53, 69, 0.08)', borderLeft: '4px solid #dc3545' };
    if (produto.diasEstoque <= 7) return { backgroundColor: 'rgba(253, 126, 20, 0.08)', borderLeft: '4px solid #fd7e14' };
    if (produto.estoque <= 3) return { backgroundColor: 'rgba(255, 193, 7, 0.08)', borderLeft: '4px solid #ffc107' };
    return {};
  };

  return (
    <Container fluid className="py-3">
      {/* Título */}
      <div className="d-flex align-items-center justify-content-between mb-4">
        <div>
          <h4 className="mb-1 fw-bold" style={{ color: '#2c3e50' }}>
            {ICONS.alerta} Alerta de Estoque — E-commerce
          </h4>
          <small className="text-muted">
            Produtos vendidos nos últimos meses com estoque zerado ou acabando
          </small>
        </div>
        {isLoading && <Spinner animation="border" size="sm" variant="primary" />}
      </div>

      {/* Cards Resumo */}
      <Row className="g-3 mb-4">
        <Col xs={6} md={3}>
          <Card
            className="h-100 border-0 shadow-sm"
            style={{
              cursor: 'pointer',
              borderLeft: filtroAtivo === 'zerados' ? '4px solid #dc3545' : 'none',
              background: filtroAtivo === 'zerados' ? 'linear-gradient(135deg, #fff5f5, #fff)' : '#fff'
            }}
            onClick={() => setFiltroAtivo(filtroAtivo === 'zerados' ? 'todos' : 'zerados')}
          >
            <Card.Body className="text-center py-3">
              <div style={{ fontSize: '2rem' }}>{ICONS.zerado}</div>
              <h2 className="mb-0 fw-bold" style={{ color: '#dc3545' }}>{resumo.zerados}</h2>
              <small className="text-muted">Estoque Zerado</small>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} md={3}>
          <Card
            className="h-100 border-0 shadow-sm"
            style={{
              cursor: 'pointer',
              borderLeft: filtroAtivo === 'acabando' ? '4px solid #ffc107' : 'none',
              background: filtroAtivo === 'acabando' ? 'linear-gradient(135deg, #fffdf5, #fff)' : '#fff'
            }}
            onClick={() => setFiltroAtivo(filtroAtivo === 'acabando' ? 'todos' : 'acabando')}
          >
            <Card.Body className="text-center py-3">
              <div style={{ fontSize: '2rem' }}>{ICONS.acabando}</div>
              <h2 className="mb-0 fw-bold" style={{ color: '#e67e22' }}>{resumo.acabando}</h2>
              <small className="text-muted">Acabando (1–3 un.)</small>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} md={3}>
          <Card
            className="h-100 border-0 shadow-sm"
            style={{
              cursor: 'pointer',
              borderLeft: filtroAtivo === 'critico' ? '4px solid #fd7e14' : 'none',
              background: filtroAtivo === 'critico' ? 'linear-gradient(135deg, #fff8f0, #fff)' : '#fff'
            }}
            onClick={() => setFiltroAtivo(filtroAtivo === 'critico' ? 'todos' : 'critico')}
          >
            <Card.Body className="text-center py-3">
              <div style={{ fontSize: '2rem' }}>{ICONS.critico}</div>
              <h2 className="mb-0 fw-bold" style={{ color: '#fd7e14' }}>{resumo.critico7d}</h2>
              <small className="text-muted">Acaba em ≤7 dias</small>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} md={3}>
          <Card
            className="h-100 border-0 shadow-sm"
            style={{
              cursor: 'pointer',
              borderLeft: filtroAtivo === 'todos' ? '4px solid #3498db' : 'none',
              background: filtroAtivo === 'todos' ? 'linear-gradient(135deg, #f0f8ff, #fff)' : '#fff'
            }}
            onClick={() => setFiltroAtivo('todos')}
          >
            <Card.Body className="text-center py-3">
              <div style={{ fontSize: '2rem' }}>{ICONS.total}</div>
              <h2 className="mb-0 fw-bold" style={{ color: '#3498db' }}>{resumo.total}</h2>
              <small className="text-muted">Total Afetados</small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Busca e Ordenação */}
      <Row className="g-3 mb-3">
        <Col md={6}>
          <InputGroup>
            <InputGroup.Text>{ICONS.busca}</InputGroup.Text>
            <Form.Control
              placeholder="Buscar por código ou nome do produto..."
              value={busca}
              onChange={(e) => handleBusca(e.target.value)}
            />
          </InputGroup>
        </Col>
        <Col md={3}>
          <Form.Select
            value={ordenacao}
            onChange={(e) => setOrdenacao(e.target.value)}
          >
            <option value="risco">Ordenar por Risco</option>
            <option value="estoque">Ordenar por Estoque</option>
            <option value="dias">Ordenar por dias p/ acabar</option>
            <option value="vendas">Ordenar por Vendas</option>
            <option value="nome">Ordenar por Nome</option>
          </Form.Select>
        </Col>
        <Col md={3}>
          <div className="d-flex align-items-center h-100">
            <small className="text-muted">
              {produtosOrdenados.length} produto{produtosOrdenados.length !== 1 ? 's' : ''} encontrado{produtosOrdenados.length !== 1 ? 's' : ''}
            </small>
          </div>
        </Col>
      </Row>

      {/* Tabela de Produtos */}
      {error ? (
        <Card className="border-0 shadow-sm">
          <Card.Body className="text-center py-5">
            <div style={{ fontSize: '3rem' }}>❌</div>
            <h5 className="mt-3">Erro ao carregar dados</h5>
            <p className="text-muted">{error.message}</p>
          </Card.Body>
        </Card>
      ) : isLoading ? (
        <Card className="border-0 shadow-sm">
          <Card.Body className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="text-muted mt-3">Carregando produtos...</p>
          </Card.Body>
        </Card>
      ) : produtosOrdenados.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <Card.Body className="text-center py-5">
            <div style={{ fontSize: '3rem' }}>✅</div>
            <h5 className="mt-3">Nenhum produto com estoque baixo</h5>
            <p className="text-muted">
              {busca ? 'Nenhum resultado para a busca.' : 'Todos os produtos estão com estoque adequado!'}
            </p>
          </Card.Body>
        </Card>
      ) : (
        <Card className="border-0 shadow-sm">
          <div className="table-responsive">
            <Table hover className="mb-0 align-middle" style={{ fontSize: '0.875rem' }}>
              <thead style={{ backgroundColor: '#f8f9fa', position: 'sticky', top: 0, zIndex: 1 }}>
                <tr>
                  <th style={{ width: 50 }}></th>
                  <th>Código</th>
                  <th>Produto</th>
                  <th className="text-center">Estoque</th>
                  <th className="text-center">Média/Mês</th>
                  <th className="text-center">Média/Dia</th>
                  <th className="text-center">Dias p/ Acabar</th>
                  <th className="text-center">Vendas 6M</th>
                  <th>Fornecedor</th>
                  <th className="text-center">Risco</th>
                </tr>
              </thead>
              <tbody>
                {produtosOrdenados.map((p) => {
                  const risco = RISCO_CONFIG[p.riscoRuptura] || RISCO_CONFIG.sem_dados;
                  return (
                    <tr key={p.codigo} style={getRowStyle(p)}>
                      <td>
                        {p.imagemUrl ? (
                          <Image
                            src={p.imagemUrl}
                            alt={p.nome}
                            width={40}
                            height={40}
                            style={{ objectFit: 'cover', borderRadius: 6 }}
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        ) : (
                          <div style={{
                            width: 40, height: 40, borderRadius: 6,
                            backgroundColor: '#e9ecef', display: 'flex',
                            alignItems: 'center', justifyContent: 'center', fontSize: '1rem'
                          }}>
                            📷
                          </div>
                        )}
                      </td>
                      <td>
                        <code style={{ fontSize: '0.8rem', color: '#6c757d' }}>{p.codigo}</code>
                      </td>
                      <td>
                        <div className="fw-semibold text-truncate" style={{ maxWidth: 250 }} title={p.nome}>
                          {p.nome}
                        </div>
                        {p.preco > 0 && (
                          <small className="text-muted">
                            R$ {p.preco.toFixed(2)}
                          </small>
                        )}
                      </td>
                      <td className="text-center">
                        <span
                          className="fw-bold"
                          style={{
                            color: p.estoque === 0 ? '#dc3545' : p.estoque <= 3 ? '#e67e22' : '#28a745',
                            fontSize: '1.1rem'
                          }}
                        >
                          {p.estoque}
                        </span>
                      </td>
                      <td className="text-center">
                        <span>{p.mediaMensal}</span>
                      </td>
                      <td className="text-center">
                        <span>{p.mediaDiaria}</span>
                      </td>
                      <td className="text-center">
                        <span
                          className="fw-bold"
                          style={{
                            color: p.diasEstoque <= 7 ? '#dc3545' : p.diasEstoque <= 15 ? '#fd7e14' : '#28a745',
                            fontSize: '1rem'
                          }}
                        >
                          {p.diasEstoque === 0 ? '🔴 Esgotado' : p.diasEstoque >= 999 ? '—' : `${p.diasEstoque}d`}
                        </span>
                      </td>
                      <td className="text-center">
                        <span className="fw-semibold">{p.totalVendas}</span>
                      </td>
                      <td>
                        <small className="text-truncate d-block" style={{ maxWidth: 120 }} title={p.fornecedor}>
                          {p.fornecedor}
                        </small>
                      </td>
                      <td className="text-center">
                        <Badge bg={risco.badgeBg} style={{ fontSize: '0.75rem' }}>
                          {risco.label}
                        </Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        </Card>
      )}
    </Container>
  );
};

export default ConferenciaEcommerce;
